/**
 * ArrivalPhase.js
 *
 * Arrival phase interface - post-voyage completion
 *
 * Features:
 * - Post-arrival prompt: "Would you like to complete your post-arrival checklist?"
 * - Voyage summary
 * - Arrival reminders (soft checklist)
 * - Links to other modules (Defects, Hours of Work)
 * - Complete voyage button
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  Clock,
  Check,
  ExternalLink,
  CheckCircle,
  ClipboardCheck,
  SkipForward
} from 'lucide-react';

// Config
import { VOYAGE_STATUS_LABELS, getOperationTemplate } from '../../../../../config/operationsConfig';

// Hooks
import { useStorageState } from '../../../../../hooks/useStorageState';
import { useVessel } from '../../../../../contexts/VesselContext';
import { portLabel } from '../../../../../utils/voyage/portLabel';

// Components
import TemplateFieldsRenderer from './TemplateFieldsRenderer';
// FT-285 Item 3: TimePickerModal replaced by InlineTimeField above the
// Complete Voyage button.
import InlineTimeField from './InlineTimeField';
import { useInlineEventTime } from './useInlineEventTime';
// Pass 2e §1.7 [v2 FIX option-2] — LIVE mount of the voyage plan card on
// ArrivalPhase so the All Fast marker is reachable while All Fast is the
// next action. Card auto-engages read-only once All Fast fires via its
// own isReadOnly memo at :630-635 — no readOnly prop needed.
import VoyagePlanUnderwayCard from './VoyagePlanUnderwayCard';

/**
 * ArrivalPhase Component
 */
const ArrivalPhase = ({ context, voyage }) => {
  const { voyage: voyageHook, logbook, showConfirm } = context;
  const { updateArrivalCheck, completeVoyage } = voyageHook;

  // Local state
  const [isCompleting, setIsCompleting] = useState(false);
  const { currentVessel } = useVessel();

  // FT-285 Item 3 + §13.9: widened timezone fallback chain to match
  // UnderwayPhase.js:81-84 / voyageBounds.js — voyage first, then vessel
  // settings, vessel timezone, UTC. Previously this file used only
  // currentVessel?.timezone || 'UTC'.
  const resolvedTimezone =
    voyage?.preferredTimezone
    || currentVessel?.settings?.timezone
    || currentVessel?.timezone
    || 'UTC';

  // FT-285 Item 3: hook instance for the Complete Voyage inline time field.
  const timeField = useInlineEventTime(resolvedTimezone);

  // Pass 2e §1.5 [v3 FIX SF1] — local container ref for the inline time field.
  // Used by §1.7 Step B's handleAllFastMarkerTap to scroll + focus the field
  // directly. ArrivalPhase's tap handler reads this ref locally from within
  // the same component — no outward-facing imperative API is exposed.
  const inlineTimeFieldContainerRef = useRef(null);

  // Post-arrival prompt state - show prompt first, then checklist if Yes
  const [showPostArrivalPrompt, setShowPostArrivalPrompt] = useState(true);
  const [showPostArrivalChecklist, setShowPostArrivalChecklist] = useState(false);

  // Load voyage settings for template fields
  const { value: voyageSettings } = useStorageState('voyage_settings', null);

  // Template fields - custom fields from phase operation template
  const [templateFieldValues, setTemplateFieldValues] = useState({});

  // Get custom template fields for post_arrival phase
  const templateFields = useMemo(() => {
    const template = getOperationTemplate('post_arrival', voyageSettings);
    return template?.fields || [];
  }, [voyageSettings]);

  // Handler for template field changes
  const handleTemplateFieldChange = useCallback((fieldId, value) => {
    setTemplateFieldValues(prev => ({ ...prev, [fieldId]: value }));
  }, []);

  // Calculate voyage duration
  const voyageDuration = useMemo(() => {
    if (!voyage.departureTime || !voyage.arrivalTime) return null;
    const start = new Date(voyage.departureTime);
    const end = new Date(voyage.arrivalTime);
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  }, [voyage.departureTime, voyage.arrivalTime]);

  // Count operations
  const operationsCount = useMemo(() => {
    return voyage.operations?.filter(op => op.status === 'completed').length || 0;
  }, [voyage.operations]);

  // Arrival checks
  const arrivalChecks = voyage.arrivalChecks || [];
  const completedChecks = arrivalChecks.filter(c => c.completed).length;

  // Handle check toggle
  const handleToggleCheck = useCallback(async (checkId, currentValue) => {
    await updateArrivalCheck(voyage.id, checkId, !currentValue);
  }, [voyage.id, updateArrivalCheck]);

  // Pass 2e §1.7 [v2 FIX option-2] — All Fast marker tap handler local to
  // ArrivalPhase. Scrolls + focuses the inline time field directly via the
  // container ref attached in §1.5. NO direct completeVoyage fire — locked
  // decision #3 (Option A). User enters the time then taps the existing
  // Complete Voyage button (handleCompleteVoyage below, UNCHANGED).
  const handleAllFastMarkerTap = useCallback(() => {
    const container = inlineTimeFieldContainerRef.current;
    if (!container) return;
    container.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const input = container.querySelector('input');
    if (input) input.focus();
  }, []);

  // FT-285 Item 3: handle complete voyage with InlineTimeField. Snapshots
  // the time at submit (freshSnapshot re-stamps Now), bails if invalid or
  // already in-flight. Replaces the previous TimePickerModal-driven flow.
  const handleCompleteVoyage = useCallback(async () => {
    if (!timeField.isValid || isCompleting) return;
    const timeOptions = timeField.freshSnapshot();
    setIsCompleting(true);
    try {
      await completeVoyage(voyage.id, timeOptions);
    } finally {
      setIsCompleting(false);
    }
  }, [voyage.id, completeVoyage, timeField, isCompleting]);

  // Render check item (standard checkbox only - fuel is captured in PreArrivalPhase)
  const renderCheckItem = (check) => {
    return (
      <button
        key={check.id}
        onClick={() => handleToggleCheck(check.id, check.completed)}
        className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
          check.completed
            ? 'bg-green-50 border-green-200'
            : 'bg-white border-gray-200 hover:border-cyan-300'
        }`}
      >
        <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
          check.completed
            ? 'bg-green-500 border-green-500'
            : 'border-gray-300'
        }`}>
          {check.completed && <Check className="h-4 w-4 text-white" />}
        </div>

        <div className="flex-1">
          <p className={`font-medium ${check.completed ? 'text-green-700' : 'text-gray-900'}`}>
            {check.text}
          </p>
        </div>

        {check.linkTo && (
          <ExternalLink className="h-4 w-4 text-gray-400" />
        )}
      </button>
    );
  };

  return (
    <div className="p-4 pb-32 lg:pb-4 space-y-4">
      {/* Pass 2e §1.7 [v2 FIX option-2] [v3 FIX SF3] — LIVE voyage plan card
          at top of ArrivalPhase. Card decides interactivity from voyage state
          via its isReadOnly memo at :630-635 — no readOnly prop. key={voyage.id}
          ensures a vessel/voyage switch on the ArrivalPhase mount point causes
          a clean remount with fresh derivation against the new voyage record.
          [v3 FIX NIT3] onTapPreArrival OMITTED — §1.1's preArrTappable gate on
          voyage.status === UNDERWAY resolves to false during ARRIVAL, so the
          card resolves the handler to undefined and the row is decorative. */}
      {voyage?.voyagePlanResolution ? (
        <VoyagePlanUnderwayCard
          key={voyage.id}
          voyage={voyage}
          onUpdatePlanItem={undefined}
          onSkipPlanItem={undefined}
          onLogPositionForWaypoint={undefined}
          onStartOperationForPlanItem={undefined}
          vesselTimezone={resolvedTimezone}
          onTapAllFast={handleAllFastMarkerTap}
        />
      ) : null}

      {/* Voyage Complete Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-lg p-4 text-white">
        <div className="flex items-center gap-3 mb-3">
          <CheckCircle className="h-8 w-8" />
          <div>
            <h2 className="text-lg font-semibold">All Fast</h2>
            <p className="text-green-100">Voyage {voyage.voyageNumber} complete</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 pt-3 border-t border-green-500/30">
          <div className="text-center">
            <p className="text-2xl font-bold">{voyageDuration}</p>
            <p className="text-xs text-green-100">Duration</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{voyage.logEntries?.length || 0}</p>
            <p className="text-xs text-green-100">Log Entries</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{operationsCount}</p>
            <p className="text-xs text-green-100">Operations</p>
          </div>
        </div>
      </div>

      {/* Voyage Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
        <h3 className="font-medium text-gray-900">Voyage Summary</h3>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Route</span>
            <span className="font-medium">{portLabel(voyage.from)} → {portLabel(voyage.to)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Departed</span>
            <span className="font-medium">
              {new Date(voyage.departureTime).toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Arrived</span>
            <span className="font-medium">
              {new Date(voyage.arrivalTime).toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Crew</span>
            <span className="font-medium">{voyage.crewCount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Passengers</span>
            <span className="font-medium">{voyage.passengerCount}</span>
          </div>
        </div>
      </div>

      {/* Arrival Reminders - Only shown if user chose "Yes" in prompt */}
      {showPostArrivalChecklist && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Post-Voyage Reminders</h3>
            <span className="text-sm text-gray-500">
              {completedChecks}/{arrivalChecks.length}
            </span>
          </div>

          <div className="p-3 space-y-2">
            {arrivalChecks.map(check => renderCheckItem(check))}

            {arrivalChecks.length === 0 && (
              <p className="text-center text-gray-400 py-4">No reminders</p>
            )}
          </div>
        </div>
      )}

      {/* Template Fields Card - Phase 6 */}
      {/* Only shown if phase operation has custom fields */}
      {templateFields.length > 0 && (
        <TemplateFieldsRenderer
          fields={templateFields}
          values={templateFieldValues}
          onChange={handleTemplateFieldChange}
          title="Additional Information"
        />
      )}

      {/* FT-285 Item 3 + Pass 2e §1.5: container ref enables ArrivalPhase's
          local All Fast marker tap handler to scroll + focus the inline time
          field. */}
      <div
        ref={inlineTimeFieldContainerRef}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
      >
        <InlineTimeField
          timeField={timeField}
          vesselTimezone={resolvedTimezone}
          label="Complete Voyage Time"
        />
      </div>

      {/* Complete Voyage Button */}
      <button
        onClick={handleCompleteVoyage}
        disabled={isCompleting || !timeField.isValid}
        className="w-full flex items-center justify-center gap-2 py-4 px-6 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 disabled:opacity-50"
        style={{ minHeight: '56px' }}
      >
        {isCompleting ? (
          <>
            <Clock className="h-5 w-5 animate-spin" />
            <span>Completing...</span>
          </>
        ) : (
          <>
            <CheckCircle className="h-5 w-5" />
            <span>Complete Voyage</span>
          </>
        )}
      </button>

      <p className="text-center text-xs text-gray-400">
        Completing will finalize this voyage record
      </p>

      {/* Post-Arrival Prompt Modal */}
      {showPostArrivalPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-xl">
            {/* Header */}
            <div className="px-5 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-8 w-8" />
                <div>
                  <h2 className="text-lg font-semibold">All Fast!</h2>
                  <p className="text-green-100 text-sm">Voyage {voyage.voyageNumber}</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 text-center">
              <ClipboardCheck className="h-12 w-12 text-cyan-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Post-Arrival Checklist
              </h3>
              <p className="text-sm text-gray-600">
                Would you like to complete your post-arrival checks now?
              </p>
            </div>

            {/* Actions */}
            <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowPostArrivalPrompt(false);
                  setShowPostArrivalChecklist(false);
                }}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-100"
              >
                <SkipForward className="h-4 w-4" />
                Skip
              </button>
              <button
                onClick={() => {
                  setShowPostArrivalPrompt(false);
                  setShowPostArrivalChecklist(true);
                }}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-cyan-600 text-white rounded-xl font-medium hover:bg-cyan-700"
              >
                <ClipboardCheck className="h-4 w-4" />
                Yes, Show Checklist
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FT-285 Item 3: TimePickerModal mount removed — replaced by
          InlineTimeField above the Complete Voyage button. */}
    </div>
  );
};

export default ArrivalPhase;
